import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAccountsApi } from '@/services/managers'

export type ResolveAccountResult = 
  | { status: 'ready'; hasAccount: true }
  | { status: 'ready'; hasAccount: false; redirectTo: 'register-business' | 'select-account' }
  | { status: 'loading' }
  | { status: 'error' }

/**
 * Resolve qual conta manager usar, sem useRouter — seguro em router.beforeEach.
 * - Se já tem accountId no store → ready, hasAccount true.
 * - Senão: GET /manager/accounts (só Bearer).
 *   - 0 contas → redirect register-business.
 *   - 1 conta → POST select automático, ready hasAccount true.
 *   - 2+ contas → auto-seleciona a última empresa usada (default_account_id do backend);
 *     sem default disponível, fallback para o seletor (select-account).
 */
export async function resolveManagerAccountContext(): Promise<ResolveAccountResult> {
  const authStore = useAuthStore()
  const accountsApi = useAccountsApi()

  if (authStore.hasAccountSelected) {
    return { status: 'ready', hasAccount: true }
  }

  try {
    const { accounts, defaultAccountId } = await accountsApi.listAccountsWithDefault()

    if (accounts.length === 0) {
      return { status: 'ready', hasAccount: false, redirectTo: 'register-business' }
    }

    if (accounts.length === 1) {
      await accountsApi.selectAccount(accounts[0].id)
      return { status: 'ready', hasAccount: true }
    }

    const target =
      defaultAccountId && accounts.some((account) => account.id === defaultAccountId)
        ? defaultAccountId
        : null
    if (!target) {
      return { status: 'ready', hasAccount: false, redirectTo: 'select-account' }
    }

    await accountsApi.selectAccount(target)
    return { status: 'ready', hasAccount: true }
  } catch {
    return { status: 'error' }
  }
}

/**
 * Garante que o gestor tenha conta selecionada no fluxo de auth (accept-terms, validate-data).
 * Usa a mesma regra que resolveManagerAccountContext; applyRedirect navega conforme redirectTo.
 */
export function useResolveManagerAccount() {
  const router = useRouter()

  const result = ref<ResolveAccountResult>({ status: 'loading' })

  const resolve = async (): Promise<ResolveAccountResult> => {
    result.value = { status: 'loading' }
    const outcome = await resolveManagerAccountContext()
    result.value = outcome
    return outcome
  }

  const applyRedirect = (): void => {
    const r = result.value
    if (r.status !== 'ready' || r.hasAccount) return
    if (r.redirectTo === 'register-business') {
      router.replace({ name: 'register-business' })
    } else if (r.redirectTo === 'select-account') {
      router.replace({
        name: 'select-account',
        query: { redirect: router.currentRoute.value.fullPath },
      })
    }
  }

  return {
    result,
    resolve,
    applyRedirect,
  }
}
